import { appConfig } from "@/libs/appConfig";
import Image from "next/image";

export type MenuItemProps = {
  name: string;
  ingredients: string;
  imageUrl: string;
  price: number;
  extraLabel: string;
  animationDelay?: number;
};

export const MenuItem: React.FC<MenuItemProps> = ({
  name,
  ingredients,
  imageUrl,
  price,
  extraLabel,
  animationDelay = 0,
}) => {
  return (
    <div
      className="group relative bg-vintage-cream/95 rounded-sm overflow-hidden
        shadow-vintage hover:shadow-[6px_6px_0px_rgba(0,0,0,0.25),0_8px_24px_rgba(0,0,0,0.15)]
        transition-all duration-300 hover:-translate-y-2"
      style={{
        animationDelay: `${animationDelay}s`,
      }}
    >
      {/* Inner border effect */}
      <div className="absolute inset-2 border border-vintage-walnut/10 pointer-events-none z-10" />

      {/* Image container */}
      <div className="relative aspect-square overflow-hidden bg-vintage-parchment">
        {imageUrl ? (
          <Image
            unoptimized={true}
            alt={name}
            src={appConfig.assetsUrl + imageUrl}
            fill
            className="object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Image
              alt="Pizza placeholder"
              src={"/pizza-placeholder.svg"}
              width={200}
              height={200}
              className="opacity-50"
            />
          </div>
        )}

        {/* Price badge - vintage stamp style */}
        {price && (
          <div className="absolute bottom-4 right-4 z-20">
            <div className="relative bg-vintage-terracotta text-vintage-cream px-4 py-2 rounded-sm shadow-stamp transform rotate-2">
              <span className="font-condensed text-xl tracking-wider">
                {price} zł
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-5 text-center">
        {/* Name */}
        <h4 className="font-display font-bold text-2xl text-vintage-espresso mb-1 leading-tight">
          {name}
        </h4>

        {/* Extra label */}
        {extraLabel && (
          <p className="font-serif italic text-vintage-walnut text-sm mb-3">
            {extraLabel}
          </p>
        )}

        {/* Decorative divider */}
        <div className="flex items-center justify-center gap-2 my-3">
          <div className="w-6 h-px bg-vintage-ochre/40" />
          <div className="w-1.5 h-1.5 bg-vintage-ochre/40 rotate-45" />
          <div className="w-6 h-px bg-vintage-ochre/40" />
        </div>

        {/* Ingredients */}
        {ingredients && (
          <div className="space-y-1">
            {ingredients.split(" · ").map((item, index) => (
              <span
                key={item}
                className="inline-block font-serif text-vintage-walnut text-sm"
              >
                {item}
                {index < ingredients.split(" · ").length - 1 && (
                  <span className="mx-2 text-vintage-ochre">·</span>
                )}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Corner decorations */}
      <div className="absolute top-3 left-3 w-4 h-4 border-l border-t border-vintage-ochre/30" />
      <div className="absolute top-3 right-3 w-4 h-4 border-r border-t border-vintage-ochre/30" />
      <div className="absolute bottom-3 left-3 w-4 h-4 border-l border-b border-vintage-ochre/30" />
      <div className="absolute bottom-3 right-3 w-4 h-4 border-r border-b border-vintage-ochre/30" />
    </div>
  );
};
